// Small, hardened per-browser UI-preference store (ADR 0120).
//
// Mirrors the ConsumerWatch sessionStorage pattern (ADR 0064) and the api.ts
// safeGet/safeSet hardening: every read/write is wrapped so a locked-down store,
// private-browsing mode, a disabled cookie/storage policy, or a quota error never
// throws (it just falls back to the default / silently no-ops). Values are JSON, and a
// stored object is shallow-merged onto the fallback so a profile written by an older
// build (missing a field a newer build added) still loads with the new default filled
// in. This is per-BROWSER, not per-user — a single "default profile" (the MVP intent);
// per-user / per-legend profiles are a deferred extension.

/** Load a JSON preference object, shallow-merged onto `fallback`. Never throws. */
export function loadPref<T extends object>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return { ...fallback, ...(parsed as Partial<T>) };
    }
    return fallback;
  } catch {
    return fallback;
  }
}

/** Persist a JSON preference object. Never throws (private mode / quota / disabled). */
export function savePref<T>(key: string, value: T): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // no-op — storage unavailable; a preference is best-effort, never load-bearing.
  }
}
